// src/features/departments/pages/EditDepartmentPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import {
  Box,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';

// Hooks
import { useDepartments } from '../hooks/useDepartment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { FormPage, FormSection, FormActions } from '../../../components/common/form';

export default function EditDepartmentPage() {
  const { t } = useTranslation(['departments', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { departmentId } = useParams();
  const { showSuccess, showError } = useSnackbar();
  
  // State
  // Name as saved, for the breadcrumb (formData.name changes while typing)
  const [savedName, setSavedName] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    cvr_number: '',
    email: '',
    customer_number: '',
    address: {
      address_line1: '',
      address_line2: '',
      postal_code: '',
      city: '',
      country: 'Denmark',
    },
    phone: {
      country_code: '+45',
      number: '',
    },
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(true);
  
  // API hooks
  const {
    getDepartmentById,
    updateDepartment,
    loading: updateLoading,
  } = useDepartments();

  // Load department data
  useEffect(() => {
    const loadDepartment = async () => {
      try {
        setLoading(true);
        const result = await getDepartmentById(departmentId);
        if (result.success) {
          const dept = result.data;
          setSavedName(dept.name);
          setFormData({
            name: dept.name || '',
            company_name: dept.company_name || '',
            cvr_number: dept.cvr_number || '',
            email: dept.email || null,
            customer_number: dept.customer_number?.toString() || '',
            address: dept.address ? {
              address_line1: dept.address.address_line1 || '',
              address_line2: dept.address.address_line2 || '',
              postal_code: dept.address.postal_code || '',
              city: dept.address.city || '',
              country: dept.address.country || 'Denmark',
            } : {
              address_line1: '',
              address_line2: '',
              postal_code: '',
              city: '',
              country: 'Denmark',
            },
            phone: dept.phone ? {
              country_code: dept.phone.country_code || '+45',
              number: dept.phone.number || '',
            } : {
              country_code: '+45',
              number: '',
            },
          });
        } else {
          showError(result.error || t('departments:errors.load_failed'));
          navigate('/departments');
        }
      } catch (error) {
        showError(t('departments:errors.load_failed'));
        navigate('/departments');
      } finally {
        setLoading(false);
      }
    };

    if (departmentId) {
      loadDepartment();
    }
  }, [departmentId, getDepartmentById, showError, navigate, t]);

  const validateForm = useCallback(() => {
    const errors = {};
    
    // Basic validation
    if (!formData.name.trim()) {
      errors.name = t('departments:form.name_required');
    } else if (formData.name.length > 100) {
      errors.name = t('departments:form.name_max_length');
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('departments:form.email_invalid');
    }

    // CVR validation (8 digits)
    if (formData.cvr_number && !/^\d{8}$/.test(formData.cvr_number)) {
      errors.cvr_number = t('departments:form.cvr_invalid');
    }

    // Customer number validation
    if (formData.customer_number && isNaN(formData.customer_number)) {
      errors.customer_number = t('departments:form.customer_number_invalid');
    }

    // Address validation (if any address field is filled)
    // Country is pre-filled, so it alone doesn't mean an address was entered
    const hasAddressData = Object.entries(formData.address).some(([key, val]) => key !== 'country' && val?.trim());
    if (hasAddressData) {
      if (!formData.address.address_line1?.trim()) {
        errors.address_line1 = t('departments:form.address_line1_required');
      }
      if (!formData.address.postal_code?.trim()) {
        errors.postal_code = t('departments:form.postal_code_required');
      }
      if (!formData.address.city?.trim()) {
        errors.city = t('departments:form.city_required');
      }
      if (!formData.address.country?.trim()) {
        errors.country = t('departments:form.country_required');
      }
    }

    // Phone validation (if any phone field is filled)
    if (formData.phone.number) {
      if (!formData.phone.country_code?.trim()) {
        errors.phone_country_code = t('departments:form.country_code_required');
      }
      if (!formData.phone.number?.trim()) {
        errors.phone_number = t('departments:form.phone_number_required');
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleChange = useCallback((field, isNested = false, nestedField = null) => (event) => {
    const value = event.target.value;
    
    if (isNested && nestedField) {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [nestedField]: value
        }
      }));
      
      if (formErrors[nestedField]) {
        setFormErrors(prev => {
          const { [nestedField]: removed, ...rest } = prev;
          return rest;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      
      if (formErrors[field]) {
        setFormErrors(prev => {
          const { [field]: removed, ...rest } = prev;
          return rest;
        });
      }
    }
  }, [formErrors]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Clean up empty objects
      const submitData = { ...formData };
      
      // Check if address has any data
      // Country is pre-filled, so it alone doesn't mean an address was entered
      const hasAddressData = Object.entries(formData.address).some(([key, val]) => key !== 'country' && val?.trim());
      if (!hasAddressData) {
        submitData.address = null;
      }
      
      // Check if phone has any data
      const hasPhoneData = formData.phone.number;
      if (!hasPhoneData) {
        submitData.phone = null;
      }

      // Convert customer_number to integer if present
      if (submitData.customer_number) {
        submitData.customer_number = parseInt(submitData.customer_number, 10);
      } else {
        submitData.customer_number = null
      }

      const result = await updateDepartment(departmentId, submitData);

      if (result.success) {
        showSuccess(t('departments:success.updated'));
        navigate('/departments');
      } else {
        showError(result.error || t('departments:errors.update_failed'));
      }
    } catch (error) {
      showError(t('departments:errors.update_failed'));
    }
  }, [validateForm, formData, updateDepartment, departmentId, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack('/departments');
  }, [navigateBack]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormPage
      title={t('departments:form.edit_title')}
      crumb={savedName}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={formErrors}
    >
      <FormSection title={t('departments:sections.basic_information')}>
          <TextField
            fullWidth
            label={t('departments:labels.name')}
            value={formData.name}
            onChange={handleChange('name')}
            error={!!formErrors.name}
            helperText={formErrors.name}
            required
          />
      </FormSection>

      {/* Company Information */}
      <FormSection title={t('departments:sections.company_information')}>
          <TextField
            fullWidth
            label={t('departments:labels.company_name')}
            value={formData.company_name}
            onChange={handleChange('company_name')}
            error={!!formErrors.company_name}
            helperText={formErrors.company_name}
          />

          <Box display="flex" gap={4} flexWrap="wrap">
            <TextField
              flex={1}
              sx={{ flex: 1 }}
              label={t('departments:labels.cvr_number')}
              value={formData.cvr_number}
              onChange={handleChange('cvr_number')}
              error={!!formErrors.cvr_number}
              helperText={formErrors.cvr_number || t('departments:form.cvr_helper')}
              placeholder="12345678"
            />

            <TextField
              flex={1}
              sx={{ flex: 1 }}
              label={t('departments:labels.customer_number')}
              value={formData.customer_number}
              onChange={handleChange('customer_number')}
              error={!!formErrors.customer_number}
              helperText={formErrors.customer_number || t('departments:form.customer_number_helper')}
              type="number"
            />
          </Box>

          <TextField
            fullWidth
            label={t('departments:labels.email')}
            value={formData.email}
            onChange={handleChange('email')}
            error={!!formErrors.email}
            helperText={formErrors.email}
            type="email"
          />
      </FormSection>

      {/* Address Information */}
      <FormSection title={t('departments:sections.address_information')}>
          <TextField
            fullWidth
            label={t('departments:labels.address_line1')}
            value={formData.address.address_line1}
            onChange={handleChange('address', true, 'address_line1')}
            error={!!formErrors.address_line1}
            helperText={formErrors.address_line1}
          />

          <TextField
            fullWidth
            label={t('departments:labels.address_line2')}
            value={formData.address.address_line2}
            onChange={handleChange('address', true, 'address_line2')}
          />

          <Box display="flex" gap={4} flexWrap="wrap">
            <TextField
              sx={{ flex: 1 }}
              label={t('departments:labels.postal_code')}
              value={formData.address.postal_code}
              onChange={handleChange('address', true, 'postal_code')}
              error={!!formErrors.postal_code}
              helperText={formErrors.postal_code}
            />

            <TextField
              sx={{ flex: 2 }}
              label={t('departments:labels.city')}
              value={formData.address.city}
              onChange={handleChange('address', true, 'city')}
              error={!!formErrors.city}
              helperText={formErrors.city}
            />
          </Box>

          <TextField
            fullWidth
            label={t('departments:labels.country')}
            value={formData.address.country}
            onChange={handleChange('address', true, 'country')}
            error={!!formErrors.country}
            helperText={formErrors.country}
          />
      </FormSection>

      {/* Phone Information */}
      <FormSection title={t('departments:sections.contact_information')}>
        <Box display="flex" gap={4} flexWrap="wrap">
          <TextField
            sx={{ flex: 1 }}
            label={t('departments:labels.country_code')}
            value={formData.phone.country_code}
            onChange={handleChange('phone', true, 'country_code')}
            error={!!formErrors.phone_country_code}
            helperText={formErrors.phone_country_code}
            placeholder="+45"
          />

          <TextField
            sx={{ flex: 3 }}
            label={t('departments:labels.phone_number')}
            value={formData.phone.number}
            onChange={handleChange('phone', true, 'number')}
            error={!!formErrors.phone_number}
            helperText={formErrors.phone_number}
            placeholder="12 34 56 78"
          />
        </Box>
      </FormSection>

      <FormActions
      onCancel={handleCancel}
      submitType="submit"
      submitLabel={updateLoading ? t('departments:actions.saving') : t('departments:actions.save')}
      submitIcon={<SaveIcon />}
      loading={updateLoading}
      />
    </FormPage>
  );
}
